import { Component, computed, inject, signal } from "@angular/core";
import { CatalogBrandExtraSegment, Marketplace, MatchType, Targeting } from "@front/m19-api-client";
import { CatalogBrandEx } from "@front/m19-models";
import { CatalogBrandService } from "@front/m19-services";
import { IButtonComponent, MODAL_DATA, ModalRef, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { CsvExportService, simpleField } from "@m19-board/services/csv-export.service";
import { KeywordListManagerComponent } from "@m19-board/shared/keyword-list-manager/keyword-list-manager.component";

export interface BrandAliasModalData {
  catalogBrand: CatalogBrandEx;
  accountId: string;
  marketplace: Marketplace;
  isReadOnly: boolean;
}

@Component({
  selector: "app-brand-alias-modal",
  templateUrl: "./brand-alias-modal.component.html",
  styleUrls: ["./brand-alias-modal.component.scss"],
  standalone: true,
  imports: [TranslocoDirective, KeywordListManagerComponent, IButtonComponent],
})
export class BrandAliasModalComponent {
  private catalogBrandService = inject(CatalogBrandService);
  private toastrService = inject(ToasterService);
  private csvExportService = inject(CsvExportService);
  private translocoService = inject(TranslocoService);

  modalData = inject(MODAL_DATA) as BrandAliasModalData;
  modalRef = inject(ModalRef);

  keywords = signal<CatalogBrandExtraSegment[]>(this.modalData.catalogBrand.extraSegments ?? []);

  toTargetings = computed(() =>
    this.keywords().map((k) => ({ targetingValue: k.keyword!, matchType: MatchType.phrase })),
  );

  close() {
    this.modalRef.close();
  }

  onKeywordsChanged(keywords: Targeting[]) {
    const toCatalogBrandExtraSegment = keywords.map((k) => ({ keyword: k.targetingValue }));
    const toAdd = toCatalogBrandExtraSegment.filter((k) => !this.keywords().some((k2) => k2.keyword === k.keyword));
    const toDelete = this.keywords().filter((k) => !toCatalogBrandExtraSegment.some((k2) => k2.keyword === k.keyword));

    if (toAdd.length > 0) {
      this.addKeyword(toAdd);
    }

    if (toDelete.length > 0) {
      this.deleteKeywords(toDelete);
    }
    this.keywords.set(toCatalogBrandExtraSegment);
  }

  downloadFile() {
    if (this.keywords().length == 0) {
      return;
    }
    this.csvExportService.exportCsv(
      {
        prefix: this.modalData.catalogBrand.brandAlias + "_other_brand_names",
      },
      this.keywords(),
      [simpleField("keyword", "OtherBrandName")],
    );
  }

  private addKeyword(toAdd: CatalogBrandExtraSegment[]) {
    this.catalogBrandService
      .addKeywordToCatalogBrand(
        this.modalData.accountId,
        this.modalData.marketplace,
        this.modalData.catalogBrand.brandId!,
        toAdd,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(this.translocoService.translate("brand-alias-modal.name_add_success"));
        },
        error: (e) => {
          this.toastrService.error(e, this.translocoService.translate("brand-alias-modal.name_add_error"));
        },
      });
  }

  private deleteKeywords(toDelete: CatalogBrandExtraSegment[]) {
    if (toDelete.length == 0) {
      return;
    }
    this.catalogBrandService
      .deleteKeywordFromCatalogBrand(
        this.modalData.accountId,
        this.modalData.marketplace,
        this.modalData.catalogBrand.brandId!,
        toDelete,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(this.translocoService.translate("brand-alias-modal.name_delete_success"));
        },
        error: (e) => {
          this.toastrService.error(e, this.translocoService.translate("brand-alias-modal.name_add_error"));
        },
      });
  }
}
